import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AlertTriangle, FilterIcon } from "../../../../images/icons/icons";
import { 
  fetchClosureExpectedDeepTrackerData,
  fetchDeepTrackerTimelineViewData,
  fetchClosurePatternAnalysisData
} from "../../../../redux/prospect-reducers/prospectSlice";
import ReactVortoxSpinner from "../../../common-components/ReactVortoxSpinner";
import { convertStraight } from "../../../common-data/Converts";
import { formatDate } from "../../../../utils/utilityfunc";

const ClosureExpectedDeepTracker = ({ userInfoData }) => {
  const dispatch = useDispatch();
  const {
    closureExpectedDeepTracker,
    closureExpectedDeepTrackerLoading,
    deepTrackerTimelineView,
    deepTrackerTimelineViewLoading,
    closurePatternAnalysis,
    closurePatternAnalysisLoading
  } = useSelector((state) => state.prospects);

  const isAdmin = userInfoData?.account_type === "admin";
  const isMarketing = userInfoData?.account_type === "marketing";
  const isClusterManager = userInfoData?.account_type === "cluster_manager";

  const [activeTab, setActiveTab] = useState("calendar");
  const [selectedDate, setSelectedDate] = useState(null);

  const tabs = [
    { id: "calendar", label: "Calendar View" },
    { id: "timeline", label: "Timeline View" },
    { id: "pattern", label: "Pattern Analysis" },
  ];

  // Filters state
  const initialFilters = {
    gym_id: !isAdmin && !isMarketing && !isClusterManager ? userInfoData?.gym_id || "" : "",
  };
  const [filters, setFilters] = useState(initialFilters);

  // // Keep filters in sync with gym_id
  // useEffect(() => {
  //   setFilters((prev) => ({
  //     ...prev,
  //     gym_id: userInfoData?.gym_id || "",
  //   }));
  // }, [userInfoData?.gym_id]);

  useEffect(() => {
    if (!userInfoData?.token) return;
    const token = userInfoData?.token;
    dispatch(
      fetchClosureExpectedDeepTrackerData({
        filters,
        token,
      })
    );
    dispatch(
      fetchDeepTrackerTimelineViewData({
        filters,
        token,
      })
    );
    dispatch(
      fetchClosurePatternAnalysisData({
        filters,
        token,
      })
    );
  }, [dispatch, userInfoData?.token, filters]);

  // Generate calendar days from API data
  const generateCalendarDays = () => {
    if (!closureExpectedDeepTracker?.data) return [];
    
    return closureExpectedDeepTracker.data.map((dateGroup, index) => {
      const date = new Date(dateGroup?.expected_closure_date);
      const day = date.getDate();
      return {
        day,
        dateGroup,
        hasStuckLeads: parseInt(dateGroup?.stuck_over_3_days) > 0,
        totalLeads: parseInt(dateGroup?.total_leads) || 0
      };
    });
  };

  // Get selected date data
  const getSelectedDateData = () => {
    if (!selectedDate || !closureExpectedDeepTracker?.data) return null;
    
    return closureExpectedDeepTracker.data.find(dateGroup => {
      const date = new Date(dateGroup?.expected_closure_date);
      return date.getDate() === selectedDate;
    });
  };

  const getLeadTypeClass = (daysInStage) => {
    const days = parseInt(daysInStage) || 0;
    if (days > 30) return "lms-closure-pipeline-tracker-premium-badge";
    if (days > 15) return "lms-closure-pipeline-tracker-corporate-badge";
    return "lms-closure-pipeline-tracker-individual-badge";
  };

  const generateTimelineEntries = () => {
    if (!deepTrackerTimelineView?.data) return [];
    
    return deepTrackerTimelineView.data.slice(0, 14).map((timelineItem) => {
      const date = new Date(timelineItem?.expected_closure_date);
     
      const dateString = formatDate(date);

      return {
        date: dateString,
        leadsStuck: parseInt(timelineItem?.stuck_leads) || 0,
        highValueAtRisk: parseInt(timelineItem?.high_value_risk) || 0,
        leadsEntered: parseInt(timelineItem?.leads_entered) || 0,
        carryForward: parseInt(timelineItem?.carry_forward) || 0,
        movedOut: parseInt(timelineItem?.moved_out) || 0,
        totalValue: timelineItem?.total_value,
      };
    });
  };

  const calendarDays = generateCalendarDays();
  console.log("calendarDays",calendarDays);
  const selectedDateData = getSelectedDateData();

  return (
    <div className="lms-closure-pipeline-tracker-closure-deep-tracker">
      {/* Tabs */}
      <div className="lms-closure-pipeline-tracker-deep-tracker-header">
        <div className="lms-closure-pipeline-tracker-deep-tracker-title-section">
          <h3 className="lms-closure-pipeline-tracker-deep-tracker-title">
            Closure Expected - Deep Tracker
          </h3>
          <p className="lms-closure-pipeline-tracker-deep-tracker-subtitle">
            Day-by-day segregation with entry date tracking and pattern analysis
          </p>
        </div>
        {/* <div className="lms-closure-pipeline-tracker-deep-tracker-filters">
          <div className="lms-closure-pipeline-tracker-stuck-only-filter">
            <FilterIcon></FilterIcon>
            <span style={{verticalAlign:"center"}} >show stuck Only</span>
          </div>
        </div> */}
      </div>
      
      <div className="lms-closure-pipeline-tracker-deep-tracker-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`lms-closure-pipeline-tracker-deep-tracker-tab ${
              activeTab === tab.id ? "active" : ""
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="lms-closure-pipeline-tracker-deep-tracker-content">
        {activeTab === "calendar" && (
          <div className="lms-closure-pipeline-tracker-calendar-view">
            {/* Calendar Grid */}
            <div className="lms-closure-pipeline-tracker-calendar-grid">
              <div className="lms-closure-pipeline-tracker-calendar-header">
                <span>Sun</span>
                <span>Mon</span>
                <span>Tue</span>
                <span>Wed</span>
                <span>Thu</span>
                <span>Fri</span>
                <span>Sat</span>
              </div>
              {closureExpectedDeepTrackerLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 180 }}>
                  <ReactVortoxSpinner />
                </div>
              ) : (
                <div className="lms-closure-pipeline-tracker-calendar-days">
                  {calendarDays.map((dayData) => (
                    <div
                      key={dayData.day}
                      className={`lms-closure-pipeline-tracker-calendar-day ${
                        selectedDate === dayData.day ? "selected" : ""
                      }`}
                      onClick={() => setSelectedDate(dayData.day)}
                    >
                      <div className="lms-closure-pipeline-tracker-calendar-day-header">
                        <span className="lms-closure-pipeline-tracker-calendar-day-number">{dayData.day}</span>
                        {dayData.hasStuckLeads && (
                          <div className="lms-closure-pipeline-tracker-calendar-warning-icon">
                            <AlertTriangle />
                          </div>
                        )}
                      </div>
                      <div className="lms-closure-pipeline-tracker-calendar-day-count">{dayData.totalLeads}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="lms-closure-pipeline-tracker-simple-view">
              {/* Date Summary Header */}
              {selectedDateData && (
                <>
                  <div className="lms-closure-pipeline-tracker-simple-header">
                    <h4 className="lms-closure-pipeline-tracker-date-title">
                      {convertStraight(selectedDateData?.expected_closure_date)}
                    </h4>
                    <div className="lms-closure-pipeline-tracker-total-value">
                      <span className="lms-closure-pipeline-tracker-total-label">Total Value : ₹  </span>
                      <span className="lms-closure-pipeline-tracker-total-amount">
                      {selectedDateData?.total_revenue}
                      </span>
                    </div>
                  </div>

                  {/* Three Column Layout */}
                  <div className="lms-closure-pipeline-tracker-simple-columns">
                    {/* Leads Entered */}
                    <div className="lms-closure-pipeline-tracker-simple-column">
                      <h4 className="lms-closure-pipeline-tracker-column-title lms-closure-pipeline-tracker-leads-entered-title">
                       Entered Leads ({selectedDateData?.total_leads || 0}) 
                      </h4>
                      <div className="lms-closure-pipeline-tracker-simple-column-content">
                        {selectedDateData?.lead_details && selectedDateData.lead_details.length > 0 ? (
                          selectedDateData.lead_details.map((lead, index) => (
                            <div key={index} className="lms-closure-pipeline-tracker-simple-lead-card">
                              <div className="lms-closure-pipeline-tracker-lead-info">
                                <p className="lms-closure-pipeline-tracker-lead-name">Member : {lead?.customer_name}</p>
                                <p className="lms-closure-pipeline-tracker-lead-agent">Agent : {lead?.agent_name}</p>
                                <p className="lms-closure-pipeline-tracker-lead-entry">Entry Date : {formatDate(lead?.entry_date)}</p>
                              </div>
                              <div className="lms-closure-pipeline-tracker-lead-stats">
                                <span
                                  className={`lms-closure-pipeline-tracker-lead-badge ${getLeadTypeClass(
                                    lead?.days_in_stage
                                  )}`}
                                >
                                 {lead?.days_in_stage} days
                                </span>
                                <span className="lms-closure-pipeline-tracker-lead-value">
                                 Total : {lead?.expected_revenue}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="lms-closure-pipeline-tracker-empty-message">No leads entered</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Carry Forward */}
                    <div className="lms-closure-pipeline-tracker-simple-column">
                      <h4 className="lms-closure-pipeline-tracker-column-title lms-closure-pipeline-tracker-carry-forward-title">
                        Carry Forward ({selectedDateData?.carry_forward || 0})
                      </h4>
                      <div className="lms-closure-pipeline-tracker-simple-column-content">
                        <p className="lms-closure-pipeline-tracker-empty-message">No carry forward data</p>
                      </div>
                    </div>

                    {/* Moved Out */}
                    <div className="lms-closure-pipeline-tracker-simple-column">
                      <h4 className="lms-closure-pipeline-tracker-column-title lms-closure-pipeline-tracker-moved-out-title">
                        Moved Out ({selectedDateData?.moved_out || 0})
                      </h4>
                      <div className="lms-closure-pipeline-tracker-simple-column-content">
                        <p className="lms-closure-pipeline-tracker-empty-message">No leads moved out</p>
                      </div>
                    </div>
                  </div>

                  {/* Detected Patterns */}
                  <div className="lms-closure-pipeline-tracker-detected-patterns">
                    <h4 className="lms-closure-pipeline-tracker-patterns-title">Detected Patterns</h4>
                    <ul className="lms-closure-pipeline-tracker-patterns-list" style={{ listStyleType: "disc", paddingLeft: "1.25rem" }}>
                      <li style={{ listStyleType: "disc" }}>{selectedDateData?.stuck_over_3_days || 0} leads stuck over 3 days</li>
                      <li style={{ listStyleType: "disc" }}>{selectedDateData?.high_value_leads || 0} high-value leads at risk</li>
                    </ul>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === "timeline" && (
          <div className="lms-closure-pipeline-tracker-timeline-view">
            {deepTrackerTimelineViewLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 160 }}>
                <ReactVortoxSpinner />
              </div>
            ) : (
              <div className="lms-closure-pipeline-tracker-timeline-entries">
                {generateTimelineEntries().map((entry, index) => (
                  <div key={index} className="lms-closure-pipeline-tracker-timeline-entry">
                    <div className="lms-closure-pipeline-tracker-timeline-left">
                      <h4 className="lms-closure-pipeline-tracker-timeline-date">Closure Expected Date : {entry.date}</h4>
                      <div className="lms-closure-pipeline-tracker-timeline-alerts">
                        {entry.leadsStuck > 0 && (
                          <span className="lms-closure-pipeline-tracker-alert-badge">
                            {entry.leadsStuck} leads stuck
                          </span>
                        )}
                        {entry.highValueAtRisk > 0 && (
                          <span className="lms-closure-pipeline-tracker-alert-badge">
                            {entry.highValueAtRisk} high-value leads at risk
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="lms-closure-pipeline-tracker-timeline-metrics">
                      <span className="lms-closure-pipeline-tracker-metric lms-closure-pipeline-tracker-metric-entered">Total : {entry.leadsEntered}</span>
                      <span className="lms-closure-pipeline-tracker-metric lms-closure-pipeline-tracker-metric-carry-forward"> Carry Forward : {entry.carryForward} </span>
                      <span className="lms-closure-pipeline-tracker-metric lms-closure-pipeline-tracker-metric-moved-out">Moved Out : {entry.movedOut}</span>
                      <span className="lms-closure-pipeline-tracker-metric lms-closure-pipeline-tracker-metric-value">Value : ₹ {entry.totalValue}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'pattern' && (
          <div className="lms-closure-pipeline-tracker-pattern-analysis-view">
            <div className="lms-closure-pipeline-tracker-pattern-panels">
              {/* Left Panel: Entry Date Analysis */}
              <div className="lms-closure-pipeline-tracker-pattern-panel">
                <h3 className="lms-closure-pipeline-tracker-panel-title">Entry Date Analysis</h3>
                 {closurePatternAnalysisLoading ? (
                   <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 140 }}>
                     <ReactVortoxSpinner />
                   </div>
                 ) : (
                   <div className="lms-closure-pipeline-tracker-panel-content">
                     {closurePatternAnalysis?.data?.entry_analysis?.slice(0, 5).map((entryItem, index) => (
                       <div key={index} className="lms-closure-pipeline-tracker-entry-analysis-item">
                         <div style={{display:"flex", flexDirection:"column", gap:"4px"}}>
                           <div className="lms-closure-pipeline-tracker-entry-date">
                             {convertStraight(entryItem?.entry_date)}
                           </div>
                           <div className="lms-closure-pipeline-tracker-entry-details">
                             {entryItem?.total_leads} leads • Avg {entryItem?.avg_days || 0} days
                           </div>
                         </div>
                         <div style={{display:"flex", flexDirection:"column", gap:"4px"}}>
                           <div className="lms-closure-pipeline-tracker-entry-value">
                           ₹  {entryItem?.total_value}
                           </div>
                           {entryItem?.avg_days > 3 && (
                             <span className="lms-closure-pipeline-tracker-risk-badge">At Risk</span>
                           )}
                         </div>
                       </div>
                     ))}
                   </div>
                 )}
              </div>

              {/* Right Panel: Risk Patterns */}
              <div className="lms-closure-pipeline-tracker-pattern-panel">
                <h3 className="lms-closure-pipeline-tracker-panel-title">Risk Patterns</h3>
                <div className="lms-closure-pipeline-tracker-panel-content">
                                     <div className="lms-closure-pipeline-tracker-risk-pattern-item high-risk">
                     <h4 className="lms-closure-pipeline-tracker-risk-title">High Risk Leads</h4>
                     <p className="lms-closure-pipeline-tracker-risk-details">
                       {closurePatternAnalysis?.data?.risk_patterns?.high_risk || 0} leads at high risk
                     </p>
                   </div>
                   <div className="lms-closure-pipeline-tracker-risk-pattern-item medium-risk">
                     <h4 className="lms-closure-pipeline-tracker-risk-title">Medium Risk Leads</h4>
                     <p className="lms-closure-pipeline-tracker-risk-details">
                       {closurePatternAnalysis?.data?.risk_patterns?.medium_risk || 0} leads at medium risk
                     </p>
                   </div>
                   <div className="lms-closure-pipeline-tracker-risk-pattern-item on-track">
                     <h4 className="lms-closure-pipeline-tracker-risk-title">On Track Leads</h4>
                     <p className="lms-closure-pipeline-tracker-risk-details">
                       {closurePatternAnalysis?.data?.risk_patterns?.on_track || 0} leads on track
                     </p>
                   </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClosureExpectedDeepTracker;
